// Lógica de la pantalla Horarios (F5 del cliente móvil): el borrador de la semana, sus errores y
// «Así queda la semana», calculados mientras el dueño edita, antes de guardar. Las reglas son las
// del servidor (`mobile/services/schedule_admin.py`), que vuelve a validar todo al guardar:
//   - un turno pertenece al día en que ARRANCA; si termina después de medianoche, la madrugada del
//     día siguiente es suya (no hace falta un turno el lunes 00:00 → 04:00 para cubrirla);
//   - hora de fin igual a la de inicio = 24 h;
//   - dos turnos no se cruzan, tampoco del domingo al lunes.

export const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
export const SHORT_DAYS = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
const DAY = 1440;
const WEEK = 7 * DAY;

export function toMinutes(time) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(String(time || ''));
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return null;
    return hours * 60 + minutes;
}

export function hhmm(minutes) {
    const m = ((minutes % DAY) + DAY) % DAY;
    return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
}

// Minutos de reloj de pared entre dos horas: fin antes que inicio cruza medianoche; iguales, 24 h.
export function durationOf(start, end) {
    const a = toMinutes(start);
    const z = toMinutes(end);
    if (a === null || z === null) return null;
    return ((z - a) % DAY + DAY) % DAY || DAY;
}

// Los turnos del servidor ([{weekday, start, end}]) como siete listas, una por día.
export function daysFromBlocks(blocks) {
    const days = DAYS.map(() => []);
    (blocks || []).forEach((b) => days[b.weekday].push({ start: b.start, end: b.end }));
    const at = (s) => { const m = toMinutes(s.start); return m === null ? 0 : m; };
    days.forEach((day) => day.sort((x, y) => at(x) - at(y)));
    return days;
}

export function blocksFromDays(days) {
    const blocks = [];
    days.forEach((day, weekday) => day.forEach((s) => blocks.push({ weekday, start: s.start, end: s.end })));
    return blocks;
}

function intervals(days) {
    const list = [];
    days.forEach((day, d) => day.forEach((s) => {
        const start = toMinutes(s.start);
        const length = durationOf(s.start, s.end);
        if (start === null || length === null) return;
        list.push({ d, s, from: d * DAY + start, to: d * DAY + start + length, start, end: start + length });
    }));
    return list;
}

function describe(x) {
    return DAYS[x.d].toLowerCase() + ' ' + x.s.start + ' → ' + hhmm(x.end);
}

// El primer problema del borrador, con el mismo texto que usaría el servidor; null si se puede guardar.
export function draftError(days) {
    // Vacía o a medio escribir (la máscara deja «hh:mm»): falta. Completa pero imposible (25:00): revisar.
    const timeProblem = (value, which, d) => {
        if (toMinutes(value) !== null) return null;
        const day = DAYS[d].toLowerCase();
        return /^\d{2}:\d{2}$/.test(String(value || ''))
            ? 'Revisa la hora de ' + which + ' de un turno del ' + day + ': va de 00:00 a 23:59.'
            : 'Falta la hora de ' + which + ' de un turno del ' + day + '.';
    };
    for (let d = 0; d < days.length; d++) {
        for (const s of days[d]) {
            const problem = timeProblem(s.start, 'inicio', d) || timeProblem(s.end, 'fin', d);
            if (problem) return problem;
        }
    }
    const list = intervals(days);
    for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
            for (const shift of [-WEEK, 0, WEEK]) {
                if (list[i].from < list[j].to + shift && list[j].from + shift < list[i].to) {
                    return 'El ' + describe(list[i]) + ' se cruza con el ' + describe(list[j]) + '.';
                }
            }
        }
    }
    return null;
}

// «termina el miércoles» o «24 h» junto a un turno; vacío si cierra el mismo día.
export function shiftNote(d, shift) {
    const start = toMinutes(shift.start);
    const length = durationOf(shift.start, shift.end);
    if (start === null || length === null) return '';
    if (length === DAY) return '24 h';
    return start + length > DAY ? 'termina el ' + DAYS[(d + 1) % 7].toLowerCase() : '';
}

// En un día cerrado: la madrugada que todavía es del turno de la víspera.
export function carryNote(days, d) {
    const prev = (d + 6) % 7;
    const ends = intervals([days[prev]]).filter((x) => x.end > DAY).map((x) => x.end);
    if (!ends.length) return '';
    return 'Hasta las ' + hhmm(Math.max(...ends)) + ' sigue el turno del ' + DAYS[prev].toLowerCase() + '.';
}

const pct = (minutes) => (minutes / DAY * 100).toFixed(3) + '%';

// «Así queda la semana»: por día de calendario, los tramos abiertos (propios, la madrugada que viene
// de la víspera y la gracia para cerrar mesas) y el texto que los resume.
export function weekRows(days, graceMinutes) {
    const grace = Math.max(0, Math.min(120, Number(graceMinutes) || 0));
    const rows = DAYS.map((name) => ({ name, segments: [], parts: [] }));
    intervals(days).forEach((x) => {
        rows[x.d].segments.push({ kind: 'shift', left: pct(x.start), width: pct(Math.min(x.end, DAY) - x.start) });
        rows[x.d].parts.push({
            at: x.start,
            text: x.end > DAY
                ? hhmm(x.start) + ' → ' + SHORT_DAYS[(x.d + 1) % 7] + ' ' + hhmm(x.end)
                : hhmm(x.start) + ' → ' + (x.end === DAY ? '24:00' : hhmm(x.end)),
        });
        if (x.end > DAY) {
            const next = rows[(x.d + 1) % 7];
            next.segments.push({ kind: 'carry', left: '0%', width: pct(x.end - DAY) });
            next.parts.push({ at: -1, text: '00:00 → ' + hhmm(x.end) + ', sigue del ' + DAYS[x.d].toLowerCase() });
        }
        if (grace > 0) {
            const day = (x.d + Math.floor(x.end / DAY)) % 7;
            const from = x.end % DAY;
            rows[day].segments.push({ kind: 'grace', left: pct(from), width: pct(Math.min(from + grace, DAY) - from) });
        }
    });
    return rows.map((row) => ({
        name: row.name,
        segments: row.segments,
        text: row.parts.sort((a, b) => a.at - b.at).map((p) => p.text).join(' · ') || 'cerrado',
    }));
}

// Día (lunes = 0) y minuto de ahora en Ecuador, sin depender de la zona del navegador.
export function ecuadorNow(date = new Date()) {
    const local = new Date(date.toLocaleString('en-US', { timeZone: 'America/Guayaquil' }));
    return { weekday: (local.getDay() + 6) % 7, minutes: local.getHours() * 60 + local.getMinutes() };
}

// Qué pasaría ahora con este borrador: abierto (y cuándo cierra), en gracia, o cerrado (y cuándo abre).
export function nowStatus(days, graceMinutes, now) {
    const grace = Math.max(0, Math.min(120, Number(graceMinutes) || 0));
    const at = now.weekday * DAY + now.minutes;
    const list = intervals(days);
    const dayOf = (m) => SHORT_DAYS[Math.floor((((m % WEEK) + WEEK) % WEEK) / DAY)];
    let state = null;
    for (const x of list) {
        for (const k of [-WEEK, 0, WEEK]) {
            if (x.from + k <= at && at < x.to + k) state = { kind: 'open', at: x.to + k };
        }
    }
    if (!state) {
        for (const x of list) {
            for (const k of [-WEEK, 0, WEEK]) {
                if (x.to + k <= at && at < x.to + k + grace) state = { kind: 'grace', at: x.to + k + grace };
            }
        }
    }
    if (state && state.kind === 'open') return { kind: 'open', label: 'abierto, cierra ' + dayOf(state.at) + ' ' + hhmm(state.at) };
    if (state) return { kind: 'grace', label: 'en gracia hasta las ' + hhmm(state.at) };
    let next = null;
    for (const x of list) {
        for (const k of [0, WEEK]) {
            if (x.from + k > at && (next === null || x.from + k < next)) next = x.from + k;
        }
    }
    if (next === null) return { kind: 'closed', label: 'cerrado toda la semana' };
    return { kind: 'closed', label: 'cerrado, abre ' + dayOf(next) + ' ' + hhmm(next) };
}

// Un Date del calendario (hora del navegador, que en el local es la de Ecuador) como lo pide la API.
export function apiDateTime(date) {
    if (!(date instanceof Date) || isNaN(date)) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
        + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes());
}
